/** Shared bits every showcase block needs. */

import { RemidusImage, type MediaItem, type RenderContext } from '@remidus/web-core'

import { videoFigure } from '../media-behaviour'

/** Blank-line-separated text into real paragraphs. */
export function paragraphs(into: HTMLElement, text: string): void {
  for (const part of text.split('\n\n')) {
    if (!part.trim()) continue
    const p = document.createElement('p')
    p.textContent = part.trim()
    into.append(p)
  }
}

/** The media ids a block names, in order, resolved and filtered.
 *
 *  Accepts both shapes because the import script writes `media_ids` and an
 *  editor adding one picture through the panel writes `media_id`. */
export function resolveAll(
  data: { media_ids?: string[] | string; media_id?: string } | undefined,
  ctx: RenderContext,
): MediaItem[] {
  const many = typeof data?.media_ids === 'string' ? [data.media_ids] : (data?.media_ids ?? [])
  const wanted = many.length ? many : [data?.media_id]
  return wanted
    .map((id) => (id ? ctx.resolveMedia(id) : undefined))
    .filter((m): m is MediaItem => Boolean(m))
}

/** An image or a video, whichever this item is. */
export function figureFor(item: MediaItem): HTMLElement {
  return item.kind === 'video' ? videoFigure(item) : RemidusImage(item)
}

/**
 * A product shot on its plinth.
 *
 * DESIGN.md, The Plinth Rule: the object is presented, never illustrated. No
 * box, no border, no rounded container — a cut-out on the page's own ground
 * with one pool of light beneath it. The pool is a sibling element rather than
 * a `box-shadow` because it is the studio lamp, not elevation.
 */
export function plinth(item: MediaItem | undefined, extra = ''): HTMLElement | null {
  if (!item) return null
  const stage = document.createElement('div')
  stage.className = `skin-plinth${extra ? ` ${extra}` : ''}`

  const pool = document.createElement('span')
  pool.className = 'skin-plinth__pool'
  pool.setAttribute('aria-hidden', 'true')

  const shot = figureFor(item)
  shot.classList.add('skin-plinth__shot')

  stage.append(pool, shot)
  return stage
}

/** The Spec Sheet Rule: label above value, mono, on a hairline. */
export function specList(
  rows: { label?: string; value?: string }[] | undefined,
  path: string,
): HTMLElement | null {
  const kept = (rows ?? []).filter((row) => row?.label)
  if (kept.length === 0) return null
  const list = document.createElement('dl')
  list.className = 'skin-specs'
  for (const [i, row] of kept.entries()) {
    const cell = document.createElement('div')
    cell.className = 'skin-specs__cell'
    const dt = document.createElement('dt')
    dt.textContent = row.label ?? ''
    dt.dataset.remField = `${path}.${i}.label`
    const dd = document.createElement('dd')
    dd.textContent = row.value ?? ''
    dd.dataset.remField = `${path}.${i}.value`
    cell.append(dt, dd)
    list.append(cell)
  }
  return list
}
